import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { Search, Home } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEntityTypes } from '@/hooks';
import { LoadingSkeleton } from '../components/shared/LoadingSkeleton';
import { filterNavigableEntityTypes } from '@/utils/entityTypeConfig';
import { multiWordMatch } from '@/utils/multiWordMatch';

export const GlobalSearchPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const { data: entityTypes, isLoading } = useEntityTypes();

  // Be defensive: ensure we operate on an array
  const typeList = filterNavigableEntityTypes(entityTypes);
  const filteredEntityTypes = typeList.filter(
    (type) =>
      multiWordMatch(`${type.display_name} ${type.entity_type}`, searchQuery),
  );

  if (isLoading) {
    return <LoadingSkeleton variant="list" count={6} />;
  }

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </Link>
          <Typography color="text.primary">Search</Typography>
        </Breadcrumbs>

        <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
          Global Search
        </Typography>

        <TextField
          fullWidth
          placeholder="Search across all entity types..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 3 }}
        />
      </Box>

      {searchQuery ? (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Entity Types ({filteredEntityTypes.length})
          </Typography>

          {filteredEntityTypes.length > 0 ? (
            <Card>
              <CardContent>
                <List>
                  {filteredEntityTypes.map((entityType) => (
                    <ListItem key={entityType.entity_type} disablePadding>
                      <ListItemButton onClick={() => navigate(`/${entityType.entity_type}`)}>
                        <ListItemText
                          primary={entityType.display_name}
                          secondary={entityType.description}
                        />
                        <Chip label={entityType.entity_type} size="small" variant="outlined" />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          ) : (
            <Alert severity="info">No entity types found matching "{searchQuery}"</Alert>
          )}
        </Box>
      ) : (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Available Entity Types
          </Typography>

          <Card>
            <CardContent>
              <List>
                {typeList.map((entityType) => (
                  <ListItem key={entityType.entity_type} disablePadding>
                    <ListItemButton onClick={() => navigate(`/${entityType.entity_type}`)}>
                      <ListItemText
                        primary={entityType.display_name}
                        secondary={entityType.description}
                      />
                      <Chip label={entityType.entity_type} size="small" variant="outlined" />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>
      )}
    </Container>
  );
};
